import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WebRTCService from '../services/WebRTCService';
import FileTransferService from '../services/FileTransferService';

const ReceivePage = () => {
  const { roomId: paramRoomId } = useParams();
  const navigate = useNavigate();
  
  const [roomId, setRoomId] = useState(paramRoomId || '');
  const [connectionStatus, setConnectionStatus] = useState('idle'); // idle, connecting, connected, receiving, completed
  const [transferProgress, setTransferProgress] = useState({ totalProgress: 0 });
  const [receivedFiles, setReceivedFiles] = useState([]);
  const [password, setPassword] = useState('');

  const webrtcRef = useRef(null);
  const fileTransferRef = useRef(null);

  // Auto-connect if roomId is in URL
  useEffect(() => {
    if (paramRoomId) {
      joinRoom();
    }
  }, [paramRoomId]);

  const joinRoom = async () => {
    if (!roomId.trim()) return;

    try {
      setConnectionStatus('connecting');

      // Initialize WebRTC
      webrtcRef.current = new WebRTCService();
      await webrtcRef.current.connect('ws://localhost:3001');
      await webrtcRef.current.joinRoom(roomId.toUpperCase(), 'Receiver');

      // Set up connection state handler
      webrtcRef.current.onConnectionChange((state) => {
        console.log('Connection state:', state);
        if (state === 'connected') {
          setConnectionStatus('connected');
          // Start receiving files
          startReceiving();
        } else if (state === 'disconnected') {
          setConnectionStatus('idle');
        }
      });

    } catch (error) {
      console.error('Error joining room:', error);
      setConnectionStatus('idle');
      alert('Failed to connect. Please check the code and try again.');
    }
  };

  const startReceiving = () => {
    if (!webrtcRef.current) return;

    setConnectionStatus('receiving');

    fileTransferRef.current = new FileTransferService(webrtcRef.current);

    fileTransferRef.current.onProgress((progress) => {
      setTransferProgress(progress);
    });

    fileTransferRef.current.onComplete((files) => {
      setConnectionStatus('completed');
      setReceivedFiles(files);
    });

    fileTransferRef.current.startReceiving();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (webrtcRef.current) {
        webrtcRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 text-center mb-8">
        Receive Files
      </h1>

      {/* Enter Code */}
      {connectionStatus === 'idle' && (
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">📥</div>
            <h2 className="text-xl font-semibold text-gray-900">Enter Share Code</h2>
            <p className="text-gray-500 mt-2">Enter the code shared by the sender</p>
          </div>

          <div className="max-w-md mx-auto">
            <input
              type="text"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value.toUpperCase())}
              placeholder="Enter 6-digit code..."
              className="w-full text-center text-3xl font-mono tracking-[0.5em] px-4 py-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 uppercase"
              maxLength={6}
            />

            <button
              onClick={joinRoom}
              disabled={roomId.length < 4}
              className="mt-6 w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Connect
            </button>
          </div>

          {/* Tips */}
          <div className="mt-8 bg-indigo-50 rounded-lg p-4">
            <h3 className="font-semibold text-indigo-900 mb-2">💡 How to receive files:</h3>
            <ol className="text-sm text-indigo-800 space-y-1 list-decimal list-inside">
              <li>Get the share code from the sender</li>
              <li>Enter the code above and click Connect</li>
              <li>Wait for the connection to establish</li>
              <li>Files will download automatically</li>
            </ol>
          </div>
        </div>
      )}

      {/* Connecting */}
      {connectionStatus === 'connecting' && (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="animate-pulse text-5xl mb-4">🔌</div>
          <h2 className="text-xl font-semibold text-gray-900">Connecting...</h2>
          <p className="text-gray-500 mt-2">Establishing secure peer-to-peer connection</p>
          
          <div className="mt-6 flex justify-center">
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Connected */}
      {connectionStatus === 'connected' && (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-xl font-semibold text-green-600">Connected!</h2>
          <p className="text-gray-500 mt-2">Waiting for file transfer to begin...</p>
        </div>
      )}

      {/* Receiving */}
      {connectionStatus === 'receiving' && (
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">📥</div>
            <h2 className="text-xl font-semibold text-gray-900">Receiving Files...</h2>
            <p className="text-gray-500 mt-2">Files are being transferred securely</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>{transferProgress.fileName || 'Receiving...'}</span>
              <span>{Math.round(transferProgress.totalProgress || 0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="bg-green-500 h-4 rounded-full transition-all duration-300"
                style={{ width: `${transferProgress.totalProgress || 0}%` }}
              />
            </div>
          </div>

          <div className="text-center text-sm text-gray-500">
            {transferProgress.bytesTransferred && transferProgress.totalBytes && (
              <span>
                {formatFileSize(transferProgress.bytesTransferred)} / {formatFileSize(transferProgress.totalBytes)}
              </span>
            )}
          </div>

          <div className="mt-6 flex items-center justify-center space-x-2 text-blue-600 bg-blue-50 rounded-lg p-3">
            <span>ℹ️</span>
            <span className="text-sm">Files will download automatically when complete</span>
          </div>
        </div>
      )}

      {/* Completed */}
      {connectionStatus === 'completed' && (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-green-600">Download Complete!</h2>
          <p className="text-gray-500 mt-2 mb-6">
            All files have been downloaded successfully.
          </p>

          {/* Downloaded Files List */}
          {receivedFiles.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Downloaded Files</h3>
              <div className="space-y-2">
                {receivedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">📄</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <span className="text-green-500">✓</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => {
              setRoomId('');
              setConnectionStatus('idle');
              setTransferProgress({ totalProgress: 0 });
              setReceivedFiles([]);
              if (webrtcRef.current) {
                webrtcRef.current.disconnect();
              }
              navigate('/receive');
            }}
            className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700"
          >
            Receive More Files
          </button>
        </div>
      )}
    </div>
  );
};

export default ReceivePage;
